import React, { useState, useEffect, useMemo } from 'react'
import { X, Clock, Gauge } from 'lucide-react'

/**
 * Duration Scale Dialog ("Alter Video Duration")
 *
 * Lets the user retime the WHOLE composition at once. Instead of editing each
 * item's start/duration by hand, they provide either:
 *   - a percentage (100% = no change, 200% = twice as long, 50% = half), or
 *   - a target duration in seconds (e.g. turn a 30s design into 60s).
 * The two inputs are linked: editing one recomputes the other from the current
 * composition duration.
 *
 * It does not mutate anything itself — it calls onConfirm({ ratio, percent,
 * targetDuration, scaleEffects }) and lets the parent apply the scaling.
 *
 * @param {boolean} isOpen
 * @param {Function} onClose
 * @param {Function} onConfirm - receives { ratio, percent, targetDuration, scaleEffects }
 * @param {number} currentDuration - current composition duration in seconds (derived from items)
 */
const DurationScaleDialog = ({ isOpen, onClose, onConfirm, currentDuration = 0 }) => {
  const [percent, setPercent] = useState(100)
  const [targetDuration, setTargetDuration] = useState(0)
  // Last field the user touched, so we know which value is authoritative.
  const [mode, setMode] = useState('percent') // 'percent' | 'target'
  // Scale animation/motion/transition/fade timings too (full fidelity).
  const [scaleEffects, setScaleEffects] = useState(true)

  const safeCurrent = Number.isFinite(currentDuration) && currentDuration > 0 ? currentDuration : 0

  // Reset to sane defaults whenever the dialog opens.
  useEffect(() => {
    if (!isOpen) return
    setPercent(100)
    setTargetDuration(Number(safeCurrent.toFixed(2)))
    setMode('percent')
    setScaleEffects(true)
  }, [isOpen, safeCurrent])

  // The authoritative ratio depends on which input the user last edited.
  const ratio = useMemo(() => {
    if (mode === 'target') {
      if (safeCurrent <= 0) return 1
      const t = parseFloat(targetDuration)
      if (!Number.isFinite(t) || t <= 0) return 1
      return t / safeCurrent
    }
    const p = parseFloat(percent)
    if (!Number.isFinite(p) || p <= 0) return 1
    return p / 100
  }, [mode, percent, targetDuration, safeCurrent])

  const resultingDuration = safeCurrent > 0 ? safeCurrent * ratio : 0
  const resultingPercent = ratio * 100

  const handlePercentChange = (value) => {
    setMode('percent')
    setPercent(value)
    const p = parseFloat(value)
    if (Number.isFinite(p) && p > 0 && safeCurrent > 0) {
      setTargetDuration(Number(((safeCurrent * p) / 100).toFixed(2)))
    }
  }

  const handleTargetChange = (value) => {
    setMode('target')
    setTargetDuration(value)
    const t = parseFloat(value)
    if (Number.isFinite(t) && t > 0 && safeCurrent > 0) {
      setPercent(Number(((t / safeCurrent) * 100).toFixed(1)))
    }
  }

  const applyPreset = (p) => handlePercentChange(p)

  const isNoop = Math.abs(ratio - 1) < 1e-6
  const isValid = Number.isFinite(ratio) && ratio > 0 && safeCurrent > 0

  const handleConfirm = () => {
    if (!isValid || isNoop) return
    onConfirm?.({
      ratio,
      percent: resultingPercent,
      targetDuration: resultingDuration,
      scaleEffects,
    })
    onClose?.()
  }

  const fmt = (s) => {
    if (!Number.isFinite(s) || s <= 0) return '0s'
    const mins = Math.floor(s / 60)
    const secs = s - mins * 60
    return mins > 0 ? `${mins}m ${secs.toFixed(1)}s` : `${secs.toFixed(2)}s`
  }

  if (!isOpen) return null

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
      <div className="bg-white rounded-xl shadow-2xl w-full max-w-md">
        {/* Header */}
        <div className="flex items-center justify-between p-5 border-b border-gray-200">
          <div className="flex items-center space-x-2">
            <div className="w-8 h-8 bg-purple-100 rounded-lg flex items-center justify-center">
              <Clock className="w-5 h-5 text-purple-600" />
            </div>
            <div>
              <h3 className="text-base font-semibold text-gray-800">Alter Video Duration</h3>
              <p className="text-xs text-gray-500">Rescale the timing of the whole design</p>
            </div>
          </div>
          <button
            onClick={onClose}
            className="p-1 rounded hover:bg-gray-100 text-gray-500"
            aria-label="Close"
          >
            <X className="w-5 h-5" />
          </button>
        </div>

        {/* Body */}
        <div className="p-5 space-y-5">
          {safeCurrent <= 0 ? (
            <div className="text-sm text-amber-700 bg-amber-50 border border-amber-200 rounded-lg p-3">
              No timed items found. Add items with a duration to the timeline first.
            </div>
          ) : (
            <>
              {/* Current duration */}
              <div className="flex items-center justify-between text-sm">
                <span className="text-gray-500">Current duration</span>
                <span className="font-medium text-gray-800">{fmt(safeCurrent)}</span>
              </div>

              {/* Percentage */}
              <div>
                <label className="flex items-center space-x-2 text-sm font-medium text-gray-700 mb-1.5">
                  <Gauge className="w-4 h-4 text-purple-600" />
                  <span>Scale (%)</span>
                </label>
                <div className="flex items-center space-x-2">
                  <input
                    type="number"
                    min="1"
                    step="1"
                    value={percent}
                    onChange={(e) => handlePercentChange(e.target.value)}
                    className="w-full px-3 py-2 text-sm border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-purple-500 focus:border-transparent"
                  />
                  <span className="text-sm text-gray-500">%</span>
                </div>
                <p className="text-[11px] text-gray-400 mt-1">
                  100% keeps it unchanged · 200% doubles · 50% halves
                </p>
              </div>

              {/* Presets */}
              <div className="flex flex-wrap gap-2">
                {[50, 75, 100, 150, 200].map((p) => (
                  <button
                    key={p}
                    onClick={() => applyPreset(p)}
                    className={`px-3 py-1 text-xs rounded-full border transition-colors ${
                      Math.round(resultingPercent) === p
                        ? 'bg-purple-600 text-white border-purple-600'
                        : 'bg-white text-gray-600 border-gray-300 hover:border-purple-400'
                    }`}
                  >
                    {p}%
                  </button>
                ))}
              </div>

              {/* Target duration */}
              <div>
                <label className="flex items-center space-x-2 text-sm font-medium text-gray-700 mb-1.5">
                  <Clock className="w-4 h-4 text-purple-600" />
                  <span>Target duration (seconds)</span>
                </label>
                <div className="flex items-center space-x-2">
                  <input
                    type="number"
                    min="0.1"
                    step="0.1"
                    value={targetDuration}
                    onChange={(e) => handleTargetChange(e.target.value)}
                    className="w-full px-3 py-2 text-sm border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-purple-500 focus:border-transparent"
                  />
                  <span className="text-sm text-gray-500">s</span>
                </div>
              </div>

              {/* Scale effects toggle */}
              <label className="flex items-start space-x-2 cursor-pointer select-none">
                <input
                  type="checkbox"
                  checked={scaleEffects}
                  onChange={(e) => setScaleEffects(e.target.checked)}
                  className="mt-0.5 w-4 h-4 text-purple-600 rounded border-gray-300 focus:ring-purple-500"
                />
                <span className="text-xs text-gray-600">
                  Also scale animation, motion, transition and fade timings
                  <span className="block text-[11px] text-gray-400">
                    Keep on so effects play at the new speed. Turn off to only shift
                    item start/duration.
                  </span>
                </span>
              </label>

              {/* Result preview */}
              <div className="bg-purple-50 border border-purple-200 rounded-lg p-3 flex items-center justify-between">
                <span className="text-sm text-purple-700">New duration</span>
                <span className="text-sm font-semibold text-purple-800">
                  {fmt(resultingDuration)}{' '}
                  <span className="text-xs font-normal text-purple-500">
                    ({Math.round(resultingPercent)}%)
                  </span>
                </span>
              </div>
            </>
          )}
        </div>

        {/* Footer */}
        <div className="flex items-center justify-end space-x-2 p-5 border-t border-gray-200">
          <button
            onClick={onClose}
            className="px-4 py-2 text-sm text-gray-600 hover:bg-gray-100 rounded-lg"
          >
            Cancel
          </button>
          <button
            onClick={handleConfirm}
            disabled={!isValid || isNoop}
            className="px-4 py-2 text-sm font-medium text-white bg-purple-600 rounded-lg hover:bg-purple-700 disabled:opacity-50 disabled:cursor-not-allowed"
          >
            Apply
          </button>
        </div>
      </div>
    </div>
  )
}

export default DurationScaleDialog
